/**
 * DECISION EPISODE — presentation only.
 *
 * Construction records decisions in meeting notes and remembers them through
 * hindsight. An episode is the opposite: the decision world FROZEN BEFORE the
 * outcome was known — what the machine held, what a human added, what they
 * committed to, and what reality later showed.
 *
 * THE RULE THAT GOVERNS EVERY LINE HERE
 *
 * An episode is AS RECORDED. Current project state must never be used to
 * redraw it. A historical record that looks live invites someone to act on a
 * fact that has since moved, and it also quietly rewrites what a person knew
 * when they decided — which is the whole thing this layer exists to protect.
 *
 * WHAT THIS MODULE REFUSES TO DO
 *
 * - It does not grade people. Calibration is about CLAIMS; the payload says so
 *   in-band with `calibratesClaimsNotPeople`.
 * - It does not read a divergence as a mistake. A human committing differently
 *   from the machine is a recorded fact, not an error, and the absence of a
 *   rationale is a named gap rather than an accusation.
 * - It does not promote a human assertion. `HUMAN_ASSERTED` and `UNVERIFIED`
 *   are carried exactly as stored; this slice has no verification path at all.
 */
import { presentContractVersion } from './runtimeVocabulary';

export const EPISODE_CONTRACT = 'decision-episode-1';

/** The surfaces that own a decision. Closed. */
export const DECISION_SURFACES = ['SCHEDULE_PROPOSAL', 'AWARD_PROPOSAL'];

export const SURFACE_LABEL = {
  SCHEDULE_PROPOSAL: 'Schedule decision',
  AWARD_PROPOSAL: 'Award decision',
};

/**
 * The closed delta vocabulary, with the wording each value has earned.
 *
 * `HUMAN_COMMITMENT_DIFFERED_FROM_MACHINE_POSITION` is the one that most needs
 * care: the backend calls it "a named gap, not an accusation", and a warning
 * tone here would make a missing note look like misconduct.
 */
export const DECISION_DELTAS = [
  'ALIGNED',
  'DIVERGED',
  'MACHINE_POSITION_CHANGED_AFTER_HUMAN_CONTEXT',
  'HUMAN_COMMITMENT_DIFFERED_FROM_MACHINE_POSITION',
  'NO_MACHINE_POSITION_INSUFFICIENT_INFORMATION',
];

const DECISION_DELTA_DISPLAY = {
  ALIGNED: {
    label: 'Human and machine agreed',
    tone: 'neutral',
    meaning: 'The commitment matched the position OMNΛS held.',
  },
  DIVERGED: {
    label: 'Human chose differently, and said why',
    tone: 'informative',
    meaning:
      'The commitment differed from the machine position, and a rationale was recorded against the closed vocabulary.',
  },
  MACHINE_POSITION_CHANGED_AFTER_HUMAN_CONTEXT: {
    label: 'OMNΛS changed its position after human context',
    tone: 'informative',
    meaning:
      'A recomputation was explicitly linked to this decision and reached a materially different position.',
  },
  HUMAN_COMMITMENT_DIFFERED_FROM_MACHINE_POSITION: {
    /**
     * Deliberately not a warning. The backend's own comment calls this "a
     * named gap, not an accusation", and the copy has to hold that line.
     */
    label: 'Human chose differently; no rationale was recorded',
    tone: 'neutral',
    meaning:
      'The commitment differed from the machine position and nobody recorded why. That is a gap in the record, not a judgement about the decision.',
  },
  NO_MACHINE_POSITION_INSUFFICIENT_INFORMATION: {
    label: 'OMNΛS held no position',
    tone: 'unknown',
    meaning:
      'There was not enough information for a machine position to stand. Abstaining is a position of its own, not a failure to compute.',
  },
};

/**
 * The delta, including its absence.
 *
 * `null` is a real answer with more than one cause — an uncommitted decision,
 * or a sent-back state the closed vocabulary has no word for. It must not read
 * as ALIGNED and must not read as an error.
 */
export const decisionDeltaDisplay = (delta) =>
  DECISION_DELTA_DISPLAY[delta] || {
    label: 'No delta is recorded',
    tone: 'unknown',
    meaning:
      'Either nobody has committed yet, or the decision was sent back for revision — states the closed vocabulary has no value for. OMNΛS does not invent one.',
  };

/**
 * The rationale categories a human may choose from. Closed, and never
 * inferred: the backend refuses an out-of-vocabulary value rather than
 * coercing it, and AI may not suggest or default one.
 */
export const RATIONALE_CATEGORIES = [
  'NEW_EVIDENCE',
  'PRIOR_EXPERIENCE',
  'RELATIONSHIP_CONTEXT',
  'SITE_CONTEXT',
  'CLIENT_DIRECTION',
  'OBJECTIVE_CHANGE',
  'MACHINE_ASSUMPTION_CHALLENGED',
  'PROFESSIONAL_JUDGMENT',
  'OTHER',
];

export const RATIONALE_CATEGORY_LABEL = {
  NEW_EVIDENCE: 'New evidence',
  PRIOR_EXPERIENCE: 'Prior experience',
  RELATIONSHIP_CONTEXT: 'Relationship context',
  SITE_CONTEXT: 'Site context',
  CLIENT_DIRECTION: 'Client direction',
  OBJECTIVE_CHANGE: 'Objective changed',
  MACHINE_ASSUMPTION_CHALLENGED: 'Machine assumption challenged',
  PROFESSIONAL_JUDGMENT: 'Professional judgement',
  OTHER: 'Other',
};

/** Calibration verdicts. Closed, and about claims rather than people. */
export const CALIBRATION_VERDICTS = [
  'SUPPORTED',
  'DIVERGED',
  'PARTIALLY_OBSERVED',
  'NOT_OBSERVABLE',
  'UNKNOWN',
];

const CALIBRATION_VERDICT_DISPLAY = {
  SUPPORTED: { label: 'Supported by what was observed', tone: 'good' },
  DIVERGED: { label: 'What was observed differed', tone: 'attention' },
  PARTIALLY_OBSERVED: { label: 'Partly observed', tone: 'attention' },
  /**
   * NOT_OBSERVABLE and UNKNOWN are distinct: one says nothing could ever
   * check this, the other says nothing has yet.
   */
  NOT_OBSERVABLE: {
    label: 'Nothing could observe this',
    tone: 'unknown',
    meaning: 'No writer exists that could ever confirm or contradict this claim.',
  },
  UNKNOWN: {
    label: 'Not yet observed',
    tone: 'unknown',
    meaning: 'Observable in principle. Nothing has recorded it yet.',
  },
};

export const calibrationVerdictDisplay = (verdict) =>
  CALIBRATION_VERDICT_DISPLAY[verdict] || CALIBRATION_VERDICT_DISPLAY.UNKNOWN;

/**
 * One human contribution, carried exactly as stored.
 *
 * `provenance` is HUMAN_ASSERTED forever — the schema has no promotion path —
 * and `verificationState` has no update path in this slice. Rendering either
 * as anything stronger would be the silent promotion the doctrine forbids.
 */
export const presentHumanContext = (record) => {
  const r = record || {};
  return {
    recordId: r.recordId || null,
    contribution: r.contribution || null,
    isDivergenceRationale: r.contribution === 'DIVERGENCE_RATIONALE',
    rationaleCategory: r.rationaleCategory || null,
    rationaleLabel: r.rationaleCategory
      ? RATIONALE_CATEGORY_LABEL[r.rationaleCategory] || r.rationaleCategory
      : null,
    /** The human's words. Never reworded, never summarised. */
    statement: r.statement || null,
    basis: r.basis || null,
    freshnessAsOf: r.freshnessAsOf || null,
    evidenceRef: r.evidenceRef || null,
    scope:
      r.scopeEntity && r.scopeId
        ? { entity: r.scopeEntity, id: r.scopeId, label: `${r.scopeEntity}:${r.scopeId}` }
        : null,
    provenance: r.provenance || 'HUMAN_ASSERTED',
    verificationState: r.verificationState || 'UNVERIFIED',
    /** Never true in this slice. Asserted so a future edit cannot drift. */
    verified: r.verificationState === 'VERIFIED',
    supersedesId: r.supersedesId || null,
    actorId: r.actorId || null,
    assertedAt: r.assertedAt || null,
    machineFingerprint: r.machineFingerprint || null,
  };
};

/**
 * Observed reality — structurally absent until the writers exist.
 *
 * UNWRITTEN with the missing writers NAMED is the honest shape. Rendering it
 * as zeros would say the work produced nothing.
 */
export const presentObservedReality = (observed) => {
  const o = observed || {};
  const writers = Array.isArray(o.missingWriters) ? o.missingWriters : [];
  return {
    status: o.status || 'UNWRITTEN',
    written: o.status === 'WRITTEN',
    missingWriters: writers.map((w) => ({
      fact: w?.fact || null,
      column: w?.column || null,
      status: w?.status || null,
      detail: w?.detail || null,
    })),
  };
};

/** One machine claim and its verdict. Claims, never people. */
export const presentCalibrationClaim = (claim) => {
  const c = claim || {};
  const verdict = CALIBRATION_VERDICTS.includes(c.verdict) ? c.verdict : 'UNKNOWN';
  return {
    claim: c.claim || null,
    value: c.value === undefined ? null : c.value,
    verdict,
    display: calibrationVerdictDisplay(verdict),
  };
};

/**
 * One episode.
 *
 * Every section is rendered from the frozen record. Nothing here reaches for
 * current project state, and `temporality` is fixed at AS_RECORDED so a
 * caller cannot accidentally mark an episode as live.
 */
export const presentEpisode = (episode) => {
  const e = episode || {};
  const v2 = e.machineStateV2 || {};
  const calibration = e.calibration || {};
  return {
    surface: e.surface || null,
    surfaceLabel: SURFACE_LABEL[e.surface] || e.surface || 'Decision',
    decisionRef: e.decisionRef || null,

    /** Fixed. An episode is never current state. */
    temporality: 'AS_RECORDED',

    machineStateV1: {
      payload: e.machineStateV1?.payload || null,
      sourceFingerprint: e.machineStateV1?.sourceFingerprint || null,
      confidence: e.machineStateV1?.confidence ?? null,
      createdById: e.machineStateV1?.createdById || null,
      createdAt: e.machineStateV1?.createdAt || null,
      present: Boolean(e.machineStateV1),
    },

    humanContext: (Array.isArray(e.humanContext) ? e.humanContext : []).map(presentHumanContext),

    machineStateV2: {
      status: v2.status || 'NOT_LINKED',
      /** Never guessed from timestamps — linkage is explicit or absent. */
      linked: v2.status === 'LINKED',
      proposalIds: Array.isArray(v2.proposalIds) ? v2.proposalIds : [],
    },

    humanCommitment: {
      state: e.humanCommitment?.state || null,
      decidedById: e.humanCommitment?.decidedById || null,
      decidedAt: e.humanCommitment?.decidedAt || null,
      note: e.humanCommitment?.note || null,
      committed: Boolean(e.humanCommitment?.decidedAt),
    },

    observedReality: presentObservedReality(e.observedReality),

    calibration: {
      /** The payload states this in-band. Carried, not assumed. */
      calibratesClaimsNotPeople: calibration.calibratesClaimsNotPeople === true,
      claims: (Array.isArray(calibration.claims) ? calibration.claims : []).map(
        presentCalibrationClaim
      ),
    },

    /** The frozen basis, verbatim. A simulation basis keeps its HYPOTHETICAL label. */
    replayContext: e.replayContext || null,
    hasReplayContext: Boolean(e.replayContext),

    delta: e.decisionDelta || null,
    deltaDisplay: decisionDeltaDisplay(e.decisionDelta),
  };
};

/** The whole response. */
export const presentEpisodes = (payload) => {
  const p = payload || {};
  const episodes = (Array.isArray(p.episodes) ? p.episodes : []).map(presentEpisode);
  return {
    contract: presentContractVersion(p.contractVersion, EPISODE_CONTRACT),
    projectId: p.projectId || null,
    episodes,
    hasEpisodes: episodes.length > 0,
  };
};

/**
 * Why the episode layer is unavailable, kept as distinct answers.
 *
 * Reading episodes needs its own capability because they can carry commercial
 * identity and relationship prose.
 */
export const presentEpisodeAvailability = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const code = (typeof data === 'object' && data && (data.code || data.detail?.code)) || null;

  if (status === 404) {
    return {
      kind: 'NOT_FOUND',
      title: 'This project was not found',
      detail: 'It may belong to another tenant, or it may not exist.',
    };
  }
  if (status === 503 || code === 'not_enabled') {
    return {
      kind: 'NOT_ENABLED',
      title: 'Decision memory is switched off',
      detail: 'A platform-wide setting. Nothing about this project caused it.',
    };
  }
  if (status === 403 && code === 'tenant_not_entitled') {
    return {
      kind: 'NOT_ENTITLED',
      title: 'This tenant is not entitled to decision memory',
      detail: 'An OMNΛS operator grants this.',
    };
  }
  if (status === 403 && code === 'capability_required') {
    return {
      kind: 'CAPABILITY_REQUIRED',
      title: 'You do not have the capability to read decision episodes',
      detail:
        'Episodes can carry commercial identity and relationship prose, so they sit behind the same access as bid pricing.',
    };
  }
  if (status === 403) {
    return {
      kind: 'ROLE_REFUSED',
      title: 'Your role cannot read decision episodes',
      detail: 'Someone with commercial access on this project can.',
    };
  }
  return {
    kind: 'UNAVAILABLE',
    title: 'Decision memory could not be loaded',
    detail: 'This is a problem reaching OMNΛS, not a statement about any decision.',
  };
};
